import { createFileRoute, Link } from "@tanstack/react-router";
import { PortalAccountMenu } from "@/components/PortalAccountMenu";
import { DemoInvestigationJourney } from "@/components/DemoInvestigationJourney";
import { useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowLeft,
  ArrowUpRight,
  CircleDot,
  Download,
  Minus,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { Line, LineChart, ResponsiveContainer } from "recharts";
import { claimsControlChart, claimsPerThousand, warrantyCostForecast } from "@/lib/warranty-data";
import {
  EXECUTIVE_PREVIEW_DISCLOSURE,
  REGULATORY_AUTHORITY_DISCLOSURE,
  REGULATORY_SYNTHETIC_DISCLOSURE,
  ExecutivePreviewNotice,
  RegulatorySyntheticPanel,
} from "@/components/ExecutiveGovernanceNotice";

export const Route = createFileRoute("/brief")({
  head: () => ({
    meta: [
      { title: "Executive Brief — Broadview Warranty Intelligence" },
      {
        name: "description",
        content:
          "Condensed one-screen executive brief: warranty cost, claims/1000, forecast variance, and top risk.",
      },
      { property: "og:title", content: "Executive Brief — Broadview" },
      {
        property: "og:description",
        content: "One-screen condensed exec brief of warranty health.",
      },
    ],
  }),
  component: ExecutiveBrief,
});

const fmtMoney = (v: number) => `$${v.toFixed(2)}M`;
const fmtPct = (v: number, d = 1) => `${v >= 0 ? "+" : ""}${v.toFixed(d)}%`;
const deltaTone = (v: number, goodNeg = true) =>
  v === 0 ? "text-muted-foreground" : v < 0 === goodNeg ? "text-success" : "text-destructive";

function DeltaIcon({ v, goodNeg = true }: { v: number; goodNeg?: boolean }) {
  if (v === 0) return <Minus className="h-3.5 w-3.5" />;
  const good = v < 0 === goodNeg;
  const UpGood = good ? ArrowDownRight : ArrowUpRight;
  const DownGood = good ? ArrowUpRight : ArrowDownRight;
  return v < 0 ? <UpGood className="h-3.5 w-3.5" /> : <DownGood className="h-3.5 w-3.5" />;
}
async function generateBriefPdf() {
  const { default: jsPDF } = await import("jspdf");

  // ---- Recompute the same metrics used on-screen ----
  const actuals = warrantyCostForecast.filter((m) => m.actual !== null);
  const last = actuals[actuals.length - 1];
  const prev = actuals[actuals.length - 2];
  const lastF = warrantyCostForecast.find((m) => m.month === last.month)!.forecast;
  const ytd = actuals.reduce((a, m) => a + (m.actual ?? 0), 0);
  const costMoM = ((last.actual! - prev.actual!) / prev.actual!) * 100;
  const fcastVar = ((last.actual! - lastF) / lastF) * 100;
  const last6 = actuals.slice(-6);
  const mape =
    (last6.reduce((a, m) => {
      const f = warrantyCostForecast.find((x) => x.month === m.month)!.forecast;
      return a + Math.abs((m.actual! - f) / m.actual!);
    }, 0) /
      last6.length) *
    100;

  const c1000 = claimsPerThousand[claimsPerThousand.length - 1].evx;
  const c1000Prev = claimsPerThousand[claimsPerThousand.length - 2].evx;
  const c1000Delta = ((c1000 - c1000Prev) / c1000Prev) * 100;
  const c1000Target = claimsPerThousand[claimsPerThousand.length - 1].target;

  const components = claimsControlChart.map((c) => {
    const vals = c.series.map((p) => p.claims);
    const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
    const mr = vals.slice(1).reduce((a, v, i) => a + Math.abs(v - vals[i]), 0) / (vals.length - 1);
    const sigma = mr / 1.128;
    const ucl = mean + 3 * sigma;
    const lcl = Math.max(0, mean - 3 * sigma);
    const breached = vals.some((v) => v > ucl || v < lcl);
    const latest = vals[vals.length - 1];
    const prior = vals[vals.length - 2];
    const mom = ((latest - prior) / prior) * 100;
    return { key: c.key, label: c.label, breached, latest, mom };
  });
  const breaches = components.filter((c) => c.breached).length;
  const topRisk = [...components].sort(
    (a, b) => b.latest * (b.mom + 100) - a.latest * (a.mom + 100),
  )[0];

  const fMoney = (v: number) => `$${v.toFixed(2)}M`;
  const fPct = (v: number, d = 1) => `${v >= 0 ? "+" : ""}${v.toFixed(d)}%`;

  // ---- Build the PDF ----
  const pdf = new jsPDF({ orientation: "p", unit: "pt", format: "a4" });
  const W = pdf.internal.pageSize.getWidth();
  const M = 40;
  let y = M;

  const line = (color: [number, number, number] = [220, 220, 224]) => {
    pdf.setDrawColor(...color);
    pdf.line(M, y, W - M, y);
  };
  const heading = (text: string, size = 11) => {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(size);
    pdf.setTextColor(20, 20, 24);
    pdf.text(text, M, y);
    y += size + 4;
  };
  const body = (text: string, size = 10, color: [number, number, number] = [60, 60, 66]) => {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(size);
    pdf.setTextColor(...color);
    const lines = pdf.splitTextToSize(text, W - M * 2);
    pdf.text(lines, M, y);
    y += lines.length * (size + 2);
  };

  // Header
  pdf.setFillColor(15, 23, 42);
  pdf.rect(0, 0, W, 60, "F");
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(14);
  pdf.setTextColor(255, 255, 255);
  pdf.text("Broadview Warranty Intelligence", M, 28);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.setTextColor(180, 190, 210);
  pdf.text(`Executive Brief · ${last.month}`, M, 46);
  pdf.setFontSize(9);
  pdf.text(`Generated ${new Date().toLocaleDateString()}`, W - M, 46, { align: "right" });

  y = 90;

  heading("Data Classification", 11);
  body(EXECUTIVE_PREVIEW_DISCLOSURE, 9, [120, 120, 130]);
  y += 8;
  line();
  y += 14;

  // Narrative
  heading("Executive Summary", 13);
  y += 2;
  body(
    `Warranty cost ${fcastVar < 0 ? "below" : "above"} AI forecast by ${Math.abs(fcastVar).toFixed(1)}% in ${last.month}. ${breaches > 0 ? `${breaches} component${breaches > 1 ? "s" : ""} breaching ±3σ — ${topRisk.label} leading.` : `All ${components.length} components in statistical control.`} Forecast MAPE ${mape.toFixed(1)}% over 6 months.`,
    10,
  );
  y += 8;
  line();
  y += 14;

  // KPI grid (2x2)
  heading("Key Metrics", 12);
  y += 4;
  const kpis: Array<[string, string, string, number]> = [
    [`Warranty Cost · ${last.month}`, fMoney(last.actual!), `YTD ${fMoney(ytd)}`, costMoM],
    ["Claims / 1,000 · EV-X", c1000.toFixed(1), `Target ≤ ${c1000Target}`, c1000Delta],
    ["Forecast Variance", fPct(fcastVar), `MAPE ${mape.toFixed(1)}%`, fcastVar],
    ["Top Risk", topRisk.label, `${topRisk.latest} claims`, topRisk.mom],
  ];
  const colW = (W - M * 2 - 12) / 2;
  const cardH = 62;
  kpis.forEach((k, i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const x = M + col * (colW + 12);
    const cy = y + row * (cardH + 10);
    pdf.setDrawColor(220, 220, 224);
    pdf.setFillColor(250, 250, 252);
    pdf.roundedRect(x, cy, colW, cardH, 4, 4, "FD");
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(120, 120, 130);
    pdf.text(k[0].toUpperCase(), x + 10, cy + 14);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(16);
    pdf.setTextColor(20, 20, 24);
    pdf.text(k[1], x + 10, cy + 34);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(120, 120, 130);
    pdf.text(k[2], x + 10, cy + 50);
    // delta
    const good = k[3] < 0;
    const dc: [number, number, number] = good
      ? [22, 163, 74]
      : k[3] > 0
        ? [220, 38, 38]
        : [120, 120, 130];
    pdf.setTextColor(dc[0], dc[1], dc[2]);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.text(fPct(k[3]), x + colW - 10, cy + 34, { align: "right" });
  });
  y += 2 * (cardH + 10) + 6;

  line();
  y += 14;

  // Health signals
  heading("Health Signals", 12);
  y += 2;
  body(
    `SPC Health: ${components.length - breaches}/${components.length} components in control` +
      (breaches > 0 ? ` · ${breaches} breaching ±3σ` : "") +
      `.\nAI Forecast Accuracy: ${(100 - mape).toFixed(1)}% (rolling 6-month MAPE ${mape.toFixed(1)}%).\nHeadline: ${fcastVar < 0 ? "Cost trending under plan." : "Cost trending over plan."} ${breaches > 0 ? `${topRisk.label} requires action.` : "No SPC interventions required."}`,
    10,
  );
  y += 10;
  line();
  y += 14;

  // Safety and regulatory domains remain decision-gated.
  heading("Safety & Regulatory Data", 12);
  body(REGULATORY_SYNTHETIC_DISCLOSURE, 10);
  body(REGULATORY_AUTHORITY_DISCLOSURE, 9, [120, 120, 130]);
  y += 8;

  // Footer
  const pageH = pdf.internal.pageSize.getHeight();
  pdf.setFont("helvetica", "italic");
  pdf.setFontSize(8);
  pdf.setTextColor(150, 150, 160);
  pdf.text("Broadview Warranty Intelligence — Executive Brief. Confidential.", W / 2, pageH - 20, {
    align: "center",
  });

  const stamp = new Date().toISOString().slice(0, 10);
  downloadPdfBlob(pdf.output("blob"), `executive-brief-${stamp}.pdf`);
}

function downloadPdfBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  // Cleanup after the browser has picked up the download.
  setTimeout(() => {
    a.remove();
    URL.revokeObjectURL(url);
  }, 1000);
}

function ExecutiveBrief() {
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await generateBriefPdf();
    } catch (err) {
      console.error("Export failed", err);
    } finally {
      setExporting(false);
    }
  };

  const s = useMemo(() => {
    const actuals = warrantyCostForecast.filter((m) => m.actual !== null);
    const last = actuals[actuals.length - 1];
    const prev = actuals[actuals.length - 2];
    const lastF = warrantyCostForecast.find((m) => m.month === last.month)!.forecast;
    const ytd = actuals.reduce((a, m) => a + (m.actual ?? 0), 0);
    const costMoM = ((last.actual! - prev.actual!) / prev.actual!) * 100;
    const fcastVar = ((last.actual! - lastF) / lastF) * 100;
    const last6 = actuals.slice(-6);
    const mape =
      (last6.reduce((a, m) => {
        const f = warrantyCostForecast.find((x) => x.month === m.month)!.forecast;
        return a + Math.abs((m.actual! - f) / m.actual!);
      }, 0) /
        last6.length) *
      100;

    const c1000 = claimsPerThousand[claimsPerThousand.length - 1].evx;
    const c1000Prev = claimsPerThousand[claimsPerThousand.length - 2].evx;
    const c1000Delta = ((c1000 - c1000Prev) / c1000Prev) * 100;
    const c1000Target = claimsPerThousand[claimsPerThousand.length - 1].target;

    const components = claimsControlChart.map((c) => {
      const vals = c.series.map((p) => p.claims);
      const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
      const mr =
        vals.slice(1).reduce((a, v, i) => a + Math.abs(v - vals[i]), 0) / (vals.length - 1);
      const sigma = mr / 1.128;
      const ucl = mean + 3 * sigma;
      const lcl = Math.max(0, mean - 3 * sigma);
      const breached = vals.some((v) => v > ucl || v < lcl);
      const latest = vals[vals.length - 1];
      const prior = vals[vals.length - 2];
      const mom = ((latest - prior) / prior) * 100;
      return { key: c.key, label: c.label, breached, latest, mom };
    });
    const breaches = components.filter((c) => c.breached).length;
    const topRisk = [...components].sort(
      (a, b) => b.latest * (b.mom + 100) - a.latest * (a.mom + 100),
    )[0];

    return {
      last,
      ytd,
      costMoM,
      fcastVar,
      mape,
      c1000,
      c1000Delta,
      c1000Target,
      components,
      breaches,
      topRisk,
    };
  }, []);

  const narrative = `Warranty cost ${s.fcastVar < 0 ? "below" : "above"} AI forecast by ${Math.abs(s.fcastVar).toFixed(1)}% in ${s.last.month}. ${s.breaches > 0 ? `${s.breaches} component${s.breaches > 1 ? "s" : ""} breaching ±3σ — ${s.topRisk.label} leading.` : `All ${s.components.length} components in statistical control.`} Forecast MAPE ${s.mape.toFixed(1)}% over 6 months.`;

  const tiles = [
    {
      label: `Warranty Cost · ${s.last.month}`,
      value: fmtMoney(s.last.actual!),
      sub: `YTD ${fmtMoney(s.ytd)}`,
      delta: s.costMoM,
      spark: warrantyCostForecast.filter((m) => m.actual !== null).map((m) => ({ y: m.actual })),
    },
    {
      label: "Claims / 1,000 · EV-X",
      value: s.c1000.toFixed(1),
      sub: `Target ≤ ${s.c1000Target}`,
      delta: s.c1000Delta,
      spark: claimsPerThousand.map((m) => ({ y: m.evx })),
    },
    {
      label: "Forecast Variance",
      value: fmtPct(s.fcastVar),
      sub: `MAPE ${s.mape.toFixed(1)}%`,
      delta: s.fcastVar,
      spark: warrantyCostForecast
        .filter((m) => m.actual !== null)
        .map((m) => ({ y: ((m.actual! - m.forecast) / m.forecast) * 100 })),
    },
    {
      label: "Top Risk",
      value: s.topRisk.label.split(" ")[0],
      sub: `${s.topRisk.latest} claims`,
      delta: s.topRisk.mom,
      spark: claimsControlChart
        .find((c) => c.key === s.topRisk.key)!
        .series.map((p) => ({ y: p.claims })),
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          html, body { background: #fff !important; }
          header.no-print, .no-print { display: none !important; }
          .print-area { max-width: none !important; padding: 0 !important; }
          section, .rounded-xl { break-inside: avoid; page-break-inside: avoid; }
        }
      `}</style>

      <header className="no-print sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1100px] flex-wrap items-center gap-4 px-6 py-4">
          <div className="flex items-center gap-2.5">
            <div
              className="flex h-9 w-9 items-center justify-center rounded-lg"
              style={{ background: "var(--gradient-primary)" }}
            >
              <ShieldCheck className="h-5 w-5 text-primary-foreground" />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-semibold">Broadview Warranty Intelligence</div>
              <div className="text-xs text-muted-foreground">Executive Brief · {s.last.month}</div>
            </div>
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <button
              onClick={handleExport}
              disabled={exporting}
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-primary/40 bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary shadow-sm transition-colors hover:border-primary hover:bg-primary hover:text-primary-foreground disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Download className="h-3.5 w-3.5" />
              {exporting ? "Exporting…" : "Export Brief"}
            </button>
            <Link
              to="/executive"
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium hover:bg-muted"
            >
              Summary
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium hover:bg-muted"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Full dashboard
            </Link>
            <PortalAccountMenu />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1100px] space-y-5 px-6 py-8 print-area">
        <ExecutivePreviewNotice />
        <DemoInvestigationJourney summaryOnly />

        <section className="rounded-xl border border-border bg-card p-5">
          <h1 className="text-xl font-semibold tracking-tight">Executive Brief</h1>
          <div className="mt-3 flex items-start gap-3 rounded-lg border border-border bg-background/60 p-3">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/15">
              <Sparkles className="h-4 w-4 text-primary" />
            </div>
            <p className="text-sm leading-relaxed">{narrative}</p>
          </div>
        </section>

        <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {tiles.map((t) => (
            <div key={t.label} className="rounded-xl border border-border bg-card p-4">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                {t.label}
              </div>
              <div className="mt-2 flex items-baseline justify-between">
                <div className="text-2xl font-semibold tabular-nums">{t.value}</div>
                <div
                  className={`flex items-center gap-0.5 text-xs font-medium ${deltaTone(t.delta)}`}
                >
                  <DeltaIcon v={t.delta} />
                  {fmtPct(t.delta)}
                </div>
              </div>
              <div className="mt-1 text-[11px] text-muted-foreground">{t.sub}</div>
              <div className="mt-3 h-10">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={t.spark} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
                    <Line
                      type="monotone"
                      dataKey="y"
                      stroke="var(--color-primary)"
                      strokeWidth={1.75}
                      dot={false}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          ))}
        </section>

        <section className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
              SPC Health
            </div>
            <div className="mt-2 text-2xl font-semibold tabular-nums">
              {s.components.length - s.breaches}
              <span className="text-muted-foreground">/{s.components.length}</span>
            </div>
            <div
              className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
                s.breaches > 0 ? "bg-destructive/15 text-destructive" : "bg-success/15 text-success"
              }`}
            >
              {s.breaches > 0 ? (
                <AlertTriangle className="h-3 w-3" />
              ) : (
                <CircleDot className="h-3 w-3" />
              )}
              {s.breaches > 0 ? `${s.breaches} breaching ±3σ` : "All in control"}
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-4">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
              AI Forecast Accuracy
            </div>
            <div className="mt-2 text-2xl font-semibold tabular-nums">
              {(100 - s.mape).toFixed(1)}%
            </div>
            <div className="mt-1 text-[11px] text-muted-foreground">
              Rolling 6-month MAPE {s.mape.toFixed(1)}%
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-4">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Headline
            </div>
            <div className="mt-2 text-sm font-medium leading-snug">
              {s.fcastVar < 0 ? "Cost trending under plan." : "Cost trending over plan."}
            </div>
            <div className="mt-1 text-[11px] text-muted-foreground">
              {s.breaches > 0
                ? `${s.topRisk.label} requires action.`
                : "No SPC interventions required."}
            </div>
          </div>
        </section>

        <RegulatorySyntheticPanel />

        <p className="text-center text-[11px] text-muted-foreground">
          Need more depth?{" "}
          <Link to="/executive" className="text-primary hover:underline">
            Executive Summary
          </Link>{" "}
          ·{" "}
          <Link to="/" className="text-primary hover:underline">
            Full Dashboard
          </Link>
        </p>
      </main>
    </div>
  );
}
